import { inject } from '@angular/core';
import { canGuard } from '@kinet-open-network/client/auth/data-access';
import { CustomersService } from '@kinet-open-network/client/customers/data-access';
import { provideDocuments } from '@kinet-open-network/client/documents/data-access';
import {
  BaseFlowsService,
  BaseFlowsStore,
  FlowTypeConfig,
  provideFlowTypeConfig,
} from '@kinet-open-network/client/flows/data-access';
import {
  changePricesStatusOptions,
  FlowType,
} from '@kinet-open-network/shared/data-access/model-pbgas';
import { map, of } from 'rxjs';
import { FlowChangePricesDetailComponent } from '../feature/flow-change-prices.detail.component';
import { FlowChangePrices } from './flow-change-prices.model';
import { FlowChangePricesService } from './flow-change-prices.service';
import { FlowChangePricesStore } from './flow-change-prices.store';

export const changePricesFlowConfig: FlowTypeConfig<FlowChangePrices> = {
  type: FlowType.changePrices,
  label: 'Cenové ponuky',
  icon: 'pi-tags',
  subject: 'ChangePrice',
  providers: [
    FlowChangePricesStore,
    { provide: BaseFlowsStore, useExisting: FlowChangePricesStore },
    { provide: BaseFlowsService, useExisting: FlowChangePricesService },
    FlowChangePricesService,
  ],
  store: FlowChangePricesStore,
  service: FlowChangePricesService,
  labels: {
    createLabel: 'Nová cenová ponuka',
    detailLabel: 'Cenová ponuka',
    listLabel: 'Cenové ponuky',
  },
  auditType: 'change-prices',
  visible: (ability) => ability.can('read', 'ChangePrice'),
  createComponent: FlowChangePricesDetailComponent,
  detailComponent: FlowChangePricesDetailComponent,
  columns: [
    { key: 'id', label: 'Číslo', filter: 'text' },
    { key: 'customerId', label: 'Číslo zákazníka', filter: 'text' },
    { key: 'customer.fullname', label: 'Zákazník', filter: 'text' },
    {
      key: 'status',
      label: 'Stav',
      filter: {
        type: 'select',
        options: changePricesStatusOptions,
        showColor: true,
      },
    },
    { key: 'offerValidTo', label: 'Platnosť CP do', filter: 'date' },
    { key: 'pricesValidFrom', label: 'Ceny platné od', filter: 'date' },
    { key: 'total', label: 'Spolu s DPH', filter: 'currency' },
    { key: 'totalBase', label: 'Spolu bez DPH', filter: 'currency' },
    { key: 'totalVat', label: 'DPH spolu', filter: 'currency' },
  ],
  detailChildrenRoutes: [
    {
      path: '',
      loadComponent: () =>
        import('../feature/tabs/flow-change-price-general-tab.component').then(
          (m) => m.FlowChangePricesGeneralTabComponent,
        ),
    },
    {
      path: 'documents',
      title: 'Dokumenty',
      providers: [
        provideDocuments(() => {
          const store = inject(FlowChangePricesStore);
          const customersService = inject(CustomersService);
          return {
            parentType: 'change-prices',
            templateContextType: 'change-prices',
            getTemplateContextDef: () => {
              const model = store.model();
              return {
                [FlowType.changePrices]: model?.id ?? '',
                customer: model?.customerId ?? '',
              };
            },
            getSendEmailOptions: () => {
              const customerId = store.model()?.customerId;
              if (!customerId) return of({ options: [] });

              return customersService.findOne(customerId).pipe(
                map((customer) => {
                  const contacts = customer.contacts ?? [];
                  const options = contacts
                    .filter((c): c is typeof c & { email: string } => !!c.email)
                    .map((c) => ({
                      label: c.contactName
                        ? `${c.contactName} <${c.email}>`
                        : c.email,
                      value: c.email,
                    }));
                  const defaultContact =
                    customer.priceContact || customer.contact;

                  return {
                    options,
                    default: defaultContact?.email ?? undefined,
                  };
                }),
              );
            },
          };
        }),
      ],
      canActivate: [canGuard('read', 'Document')],
      loadChildren: () =>
        import('@kinet-open-network/client/documents/feature/shell'),
    },
  ],
  createChildrenRoutes: [
    {
      path: '',
      loadComponent: () =>
        import('../feature/tabs/flow-change-price-general-tab.component').then(
          (m) => m.FlowChangePricesGeneralTabComponent,
        ),
    },
  ],
};

export function provideChangePricesFlowConfig() {
  return provideFlowTypeConfig(changePricesFlowConfig);
}
